import { Dispatch, SetStateAction, useState } from "react";
import { Button } from "reactstrap";

import AppDrawer from "../../Drawers/AppDrawer";
import EmailGenerator from "../../../views/brie/BrieAITools/OpenAIPowered/EmailGenerator";
import CustomPrompt from "../../../views/brie/BrieAITools/OpenAIPowered/CustomPrompt";
import TranslateText from "../../../views/brie/BrieAITools/OpenAIPowered/TranslateText";
import IconCard from "../../../views/workflows/IconCard";
import IconCardSmall from "../../../views/workflows/IconCardSmall";

export type BrieComposePage =
  | "Selection"
  | "Email Generator"
  | "Custom Prompt"
  | "Translate";

export function BrieComposeDrawer({
  open,
  setOpen,
  description,
  providedText,
  onSave,
  onDrawerClose,
}: {
  open: boolean;
  setOpen: any;
  description?: string;
  providedText?: string;
  onSave: (text: string) => void;
  onDrawerClose?: () => void;
}) {
  const [page, setPage] = useState<BrieComposePage>("Selection");
  const [generatedText, setGeneratedText] = useState("");

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      headerTitle="Brie Compose"
      headerDescription={description}
      headerOptions={
        <>
          {page !== "Selection" && (
            <>
              <Button
                size="sm"
                onClick={() => {
                  setPage("Selection");
                  setGeneratedText("");
                }}
              >
                Go Back
              </Button>
              <Button
                size="sm"
                color="primary"
                onClick={() => {
                  const finalText = generatedText.replace(/\r?\n/g, "<br />");
                  onSave(finalText);
                  handleClose();
                }}
                disabled={!generatedText}
                style={{ width: 80 }}
              >
                Insert
              </Button>
            </>
          )}
        </>
      }
      bodyCSS="pt-3 pb-6"
      body={
        <>
          <BrieComposeBody
            page={page}
            setPage={setPage}
            generatedText={generatedText}
            setGeneratedText={setGeneratedText}
            providedText={providedText}
          />
        </>
      }
    />
  );

  function handleClose() {
    setOpen(false);
    setPage("Selection");
    setGeneratedText("");
    onDrawerClose && onDrawerClose();
  }
}

export function BrieComposeBody({
  page = "Selection",
  setPage,
  generatedText,
  setGeneratedText,
  providedText,
  simplified = false,
}: {
  setPage: Dispatch<SetStateAction<BrieComposePage>>;
  page: BrieComposePage;
  generatedText: string;
  setGeneratedText: Dispatch<SetStateAction<string>>;
  providedText?: string;
  simplified?: boolean;
}) {
  const Card = simplified ? IconCardSmall : IconCard;

  return (
    <>
      {page === "Selection" && (
        <>
          {/* @ts-ignore */}
          <Card
            icon={"mdi mdi-email-outline"}
            name={"Email Generator"}
            description={
              simplified ? "" : "Generates draft emails within seconds"
            }
            css={simplified ? "mb-1 mt--3" : "mb-3"}
            isSelected={false}
            onClick={() => setPage("Email Generator")}
            iconOutline={false}
          />
          {/* @ts-ignore */}
          <Card
            icon={"mdi mdi-mail"}
            name={"Custom Prompt"}
            description={
              simplified
                ? ""
                : "Get exactly what you want! Enter your own unique prompt, and let our AI generate content tailored specifically to your needs"
            }
            css={simplified ? "mb-1" : "mb-3"}
            isSelected={false}
            onClick={() => setPage("Custom Prompt")}
            iconOutline={false}
          />
          {/* @ts-ignore */}
          <Card
            icon={"mdi mdi-translate"}
            name={"Translate"}
            description={
              simplified
                ? ""
                : "Enter a text or prompt to translate it in real tiem, just ask the AI model which language you need."
            }
            css="mb-3"
            isSelected={false}
            onClick={() => setPage("Translate")}
            iconOutline={false}
          />
        </>
      )}
      {page === "Email Generator" && (
        <EmailGenerator
          providedStateValue={generatedText}
          providedStateSetter={setGeneratedText}
          simplified={simplified}
        />
      )}
      {page === "Custom Prompt" && (
        <CustomPrompt
          providedStateValue={generatedText}
          providedStateSetter={setGeneratedText}
          simplified={simplified}
        />
      )}
      {page === "Translate" && (
        <TranslateText
          providedText={providedText}
          providedStateValue={generatedText}
          providedStateSetter={setGeneratedText}
        />
      )}
    </>
  );
}
